/**
 * S-01 «Настройки» — корневая вкладка (изменение v1.2), как в Telegram.
 *
 * Изменение v1.15 — шапка профиля по образцу Telegram: обложка с фоном из
 * аватара (размытый снимок, затемнение по его яркости; без фото — цвет
 * профиля), слева в шапке QR-код (S-27), справа «Изменить» (U-03). Аватар
 * открывает просмотр фото, «Изменить фото» меняет его прямо здесь — без
 * отдельного раздела.
 *
 * Список разобран по смыслу и стал короче вдвое: всё про учётную запись
 * (способы входа, ограничения, данные, удаление) — внутри «Аккаунта»,
 * заблокированные — в «Приватности и безопасности», документы — в «Помощи».
 * Группы без заголовков, как в Telegram: смысл несут плитки и порядок.
 * ПС §15: критические настройки по-прежнему в один-два шага от корня.
 */
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { ProfileHero, useCoverPalette } from '@entities/user/ProfileView'
import { useInstallOffer } from '@features/pwa/use-install'
import { Button, Dialog, Icon, cx, type IconName } from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import { logoutAndNavigate } from '../../boot/session-lifecycle'
import { useAppStore } from '../../model/app-store'
import { hotkeysAvailable } from '../../model/hotkeys'
import { useThemeStore } from '../../model/theme'
import { useProfilePhotos } from '../profile/use-profile-photos'
import { InstallInstructions } from '../system/InstallApp'
import { TONE_CLASS, type SettingsTone } from './tone'
import styles from './settings.module.css'

const THEME_LABEL = { light: 'Светлая', dark: 'Тёмная', system: 'Системная' } as const

interface Row {
  id: string
  label: string
  icon: IconName
  tone: SettingsTone
  value?: string
  /** Значение — предупреждением (ограничение аккаунта). */
  attention?: boolean
  onOpen: () => void
}

export function SettingsScreen(): React.ReactElement {
  const navigate = useNavigate()
  const me = useAppStore((s) => s.me)
  const theme = useThemeStore((s) => s.preference)
  const [logoutOpen, setLogoutOpen] = useState(false)
  const photos = useProfilePhotos()
  const palette = useCoverPalette(me?.profile.photos ?? [], me?.user.id)

  /*
   * PWA-07: постоянный пункт «Установить приложение». В standalone и после
   * установки пункт скрывается (§9.4 п. 2, ветка `appinstalled`).
   */
  const install = useInstallOffer(me?.user.id ?? null)

  // §4.6 п. 2: подсказка сочетаний доступна и из меню приложения, не только по `?`.
  const setShortcutsOpen = useAppStore((s) => s.setShortcutsOpen)
  const showHotkeys = hotkeysAvailable()

  const profile = me?.profile ?? null
  const primaryIdentity = me?.identities.find((identity) => identity.is_primary) ?? me?.identities[0] ?? null
  const section = (id: string) => () => void navigate({ to: '/settings/$section', params: { section: id } })

  const uploading =
    photos.upload.stage !== 'idle' && photos.upload.stage !== 'failed' && photos.upload.stage !== 'rejected'

  const accountRows: Row[] = [
    {
      id: 'account',
      label: 'Аккаунт',
      icon: 'user',
      tone: 'blue',
      // Ограничение важнее номера: о нём нужно узнать, не открывая раздел
      value: me?.sanctions.length ? 'есть ограничение' : primaryIdentity?.masked,
      attention: Boolean(me?.sanctions.length),
      onOpen: section('account'),
    },
    { id: 'sessions', label: 'Устройства', icon: 'monitor', tone: 'orange', onOpen: section('sessions') },
    {
      id: 'folders',
      label: 'Папки чатов',
      icon: 'archive',
      tone: 'sky',
      onOpen: () => void navigate({ to: '/settings/folders' }),
    },
  ]

  const appRows: Row[] = [
    {
      id: 'notifications',
      label: 'Уведомления',
      icon: 'bell',
      tone: 'red',
      onOpen: section('notifications'),
    },
    {
      id: 'privacy',
      label: 'Приватность и безопасность',
      icon: 'lock',
      tone: 'gray',
      onOpen: section('privacy'),
    },
    {
      id: 'appearance',
      label: 'Внешний вид и язык',
      icon: 'sun',
      tone: 'purple',
      value: THEME_LABEL[theme],
      onOpen: section('appearance'),
    },
  ]

  const helpRows: Row[] = [
    { id: 'support', label: 'Помощь и поддержка', icon: 'info', tone: 'green', onOpen: section('support') },
  ]
  if (showHotkeys) {
    helpRows.push({
      id: 'hotkeys',
      label: 'Горячие клавиши',
      icon: 'grid',
      tone: 'gray',
      onOpen: () => setShortcutsOpen(true),
    })
  }
  if (install.variant !== 'none') {
    helpRows.push({
      id: 'install',
      label: 'Установить приложение',
      icon: 'download',
      tone: 'blue',
      // В Chromium — системный диалог; в мобильной Safari — инструкция PWA-02.
      onOpen: () => (install.variant === 'chromium' ? void install.install() : install.openInstructions()),
    })
  }

  const meta = [primaryIdentity?.masked, profile?.username ? `@${profile.username}` : null].filter(Boolean)

  return (
    <Screen
      title="Настройки"
      narrow
      leading={
        <button type="button" aria-label="QR-код" onClick={() => void navigate({ to: '/settings/qr' })}>
          <Icon name="qr" size={22} />
        </button>
      }
      actions={
        profile ? (
          <Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/me/edit' })}>
            Изменить
          </Button>
        ) : null
      }
      cover={
        profile ? (
          <ProfileHero
            name={profile.display_name}
            age={profile.age}
            photos={profile.photos}
            status={meta.join(' · ') || null}
            palette={palette}
            onPhotoClick={(index) => photos.openViewer(index)}
          />
        ) : null
      }
      coverColor={profile ? palette.top : null}
    >
      {/* Быстрые действия — акцентные строки, как «Change Profile Photo» / «Set Username» */}
      {profile ? (
        <div className={cx(styles['card'], styles['group'])}>
          <button
            type="button"
            className={styles['quickRow']}
            disabled={uploading}
            onClick={() => photos.choose('main')}
          >
            <span className={styles['quickIcon']}>
              <Icon name="camera" size={22} />
            </span>
            <span className={styles['quickLabel']}>
              {uploading
                ? photos.upload.stage === 'uploading'
                  ? `Загружаем фото… ${Math.round(photos.upload.progress * 100)}%`
                  : 'Обрабатываем фото…'
                : profile.photos.length
                  ? 'Изменить фото'
                  : 'Выбрать фото'}
            </span>
          </button>
          {!profile.username ? (
            <button
              type="button"
              className={styles['quickRow']}
              onClick={() => void navigate({ to: '/me/username' })}
            >
              <span className={styles['quickIcon']}>
                <Icon name="at" size={22} />
              </span>
              <span className={styles['quickLabel']}>Задать никнейм</span>
            </button>
          ) : null}
        </div>
      ) : null}

      {profile ? (
        <SettingsGroup
          rows={[
            {
              id: 'profile',
              label: 'Мой профиль',
              icon: 'user',
              tone: 'red',
              value: profile.completeness.percent < 100 ? `${profile.completeness.percent}%` : undefined,
              onOpen: () => void navigate({ to: '/me' }),
            },
          ]}
        />
      ) : null}

      <SettingsGroup rows={accountRows} />
      <SettingsGroup rows={appRows} />
      <SettingsGroup rows={helpRows} />

      <InstallInstructions
        open={install.instructionsOpen}
        onClose={install.closeInstructions}
        onAlreadyInstalled={() => {
          install.hideForever()
          install.closeInstructions()
        }}
      />

      {/* «Выйти» — отдельно и по центру, как Log Out в Telegram: не нажать случайно рядом с другим */}
      <div className={cx(styles['card'], styles['group'])}>
        <button type="button" className={styles['logoutRow']} onClick={() => setLogoutOpen(true)}>
          Выйти
        </button>
      </div>

      <p className={styles['version']}>Движ · веб-клиент {__APP_VERSION__}</p>

      {photos.ui}

      {/* S-24: выход — подтверждаем, чтобы не потерять черновики случайно */}
      <Dialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        title="Выйти из аккаунта?"
        description="Локальные данные этого устройства будут удалены. Неотправленные сообщения пропадут."
        footer={
          <>
            <Button variant="secondary" onClick={() => setLogoutOpen(false)}>
              Остаться
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setLogoutOpen(false)
                void logoutAndNavigate(() => navigate({ to: '/', replace: true }))
              }}
            >
              Выйти
            </Button>
          </>
        }
      />
    </Screen>
  )
}

function SettingsGroup({ rows }: { rows: Row[] }) {
  if (!rows.length) return null

  return (
    <div className={cx(styles['card'], styles['group'])}>
      {rows.map((row) => (
        <button key={row.id} type="button" className={styles['row']} onClick={row.onOpen}>
          <span className={cx(styles['rowIcon'], styles[TONE_CLASS[row.tone]])}>
            <Icon name={row.icon} size={18} />
          </span>

          <span className={styles['rowBody']}>
            <span className={styles['rowLabel']}>{row.label}</span>
            {row.value ? (
              <span className={cx(styles['rowValue'], row.attention && styles['rowValueAttention'])}>
                {row.value}
              </span>
            ) : null}
          </span>

          <span className={styles['rowChevron']}>
            <Icon name="chevron-right" size={18} />
          </span>
        </button>
      ))}
    </div>
  )
}
